import React from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Checkbox,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@diligentcorp/atlas-react-icons/dist/esm/lens/Close.js";
import { DesktopDatePicker } from "@mui/x-date-pickers-pro";
import type {
  FilterValuesResponse,
  FilterOption,
} from "../services/filterValuesService";
import type { AdvancedSearchParams } from "../types/search";

interface SearchFilterProps {
  open: boolean;
  onClose: () => void;
  onApply: (params: AdvancedSearchParams) => void;
  filterValues?: FilterValuesResponse | null;
  isLoading?: boolean;
}

const SearchFilter: React.FC<SearchFilterProps> = ({
  open,
  onClose,
  onApply,
  filterValues,
  isLoading,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [policyCode, setPolicyCode] = React.useState<string[]>([]);
  const [documentType, setDocumentType] = React.useState<string[]>([]);
  const [documentCategory, setDocumentCategory] = React.useState<string[]>([]);
  const [documentSubCategory, setDocumentSubCategory] = React.useState<
    string[]
  >([]);
  const [policyLanguage, setPolicyLanguage] = React.useState<string[]>([]);
  const [publishedSince, setPublishedSince] = React.useState<Date | null>(null);

  // Do not reset fields on close; keep selections persistent across openings

  const extractKeys = (opts?: FilterOption[]): string[] => {
    if (!opts) return [];
    return opts.flatMap((o) => Object.keys(o));
  };

  const policyCodes = extractKeys(filterValues?.policyCode);
  const documentTypes = extractKeys(filterValues?.documentType);
  const documentCategories = extractKeys(filterValues?.documentCategory);
  const documentSubCategories = extractKeys(filterValues?.documentSubCategory);
  const languages = extractKeys(filterValues?.documentLanguage);
  // Use raw languages from API without any transformations

  const handleApply = () => {
    const params: AdvancedSearchParams = {
      policyCode: policyCode.length ? policyCode.join(",") : "",
      documentType: documentType.length ? documentType.join(",") : "",
      documentCategory: documentCategory.length
        ? documentCategory.join(",")
        : "",
      documentSubCategory: documentSubCategory.length
        ? documentSubCategory.join(",")
        : "",
      policyLanguage: policyLanguage.length ? policyLanguage.join(",") : "",
      publishedSince,
    };
    // Close immediately but retain selections; parent may also close
    onClose();
    onApply(params);
  };

  const handleClear = () => {
    setPolicyCode([]);
    setDocumentType([]);
    setDocumentCategory([]);
    setDocumentSubCategory([]);
    setPolicyLanguage([]);
    setPublishedSince(null);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            width: isMobile ? "100vw" : 360,
            top: 64,
            height: isMobile ? "calc(100vh - 64px)" : "calc(100vh - 64px)",
            borderRadius: isMobile ? 0 : undefined,
          },
        },
      }}
      ModalProps={{
        BackdropProps: {
          sx: {
            top: 64,
            height: "calc(100vh - 64px)",
          },
        },
      }}
    >
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 2,
          }}
        >
          <Typography variant="h3" sx={{ fontWeight: 600 }}>
            Filters
          </Typography>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon size="lg" />
          </IconButton>
        </Box>

        <Box
          sx={{
            flex: 1,
            px: 2,
            pb: 2,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            overflowY: "auto",
          }}
        >
          {isLoading ? (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <>
              <FormControl fullWidth>
                <InputLabel>Policy code</InputLabel>
                <Select
                  multiple
                  value={policyCode}
                  label="Policy code"
                  onChange={(e) =>
                    setPolicyCode(
                      typeof e.target.value === "string"
                        ? e.target.value.split(",")
                        : (e.target.value as string[])
                    )
                  }
                  displayEmpty
                  renderValue={(selected) =>
                    (selected as string[]).length
                      ? (selected as string[]).join(", ")
                      : "Choose policy code"
                  }
                >
                  {policyCodes.map((code) => (
                    <MenuItem key={code} value={code}>
                      <Checkbox
                        checked={policyCode.indexOf(code) > -1}
                        sx={{ mr: 1 }}
                      />
                      {code}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Document type</InputLabel>
                <Select
                  multiple
                  value={documentType}
                  label="Document type"
                  onChange={(e) =>
                    setDocumentType(
                      typeof e.target.value === "string"
                        ? e.target.value.split(",")
                        : (e.target.value as string[])
                    )
                  }
                  displayEmpty
                  renderValue={(selected) =>
                    (selected as string[]).length
                      ? (selected as string[]).join(", ")
                      : "Choose document type"
                  }
                >
                  {documentTypes.map((t) => (
                    <MenuItem key={t} value={t}>
                      <Checkbox
                        checked={documentType.indexOf(t) > -1}
                        sx={{ mr: 1 }}
                      />
                      {t}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Document category</InputLabel>
                <Select
                  multiple
                  value={documentCategory}
                  label="Document category"
                  onChange={(e) =>
                    setDocumentCategory(
                      typeof e.target.value === "string"
                        ? e.target.value.split(",")
                        : (e.target.value as string[])
                    )
                  }
                  displayEmpty
                  renderValue={(selected) =>
                    (selected as string[]).length
                      ? (selected as string[]).join(", ")
                      : "Choose document category"
                  }
                >
                  {documentCategories.map((c) => (
                    <MenuItem key={c} value={c}>
                      <Checkbox
                        checked={documentCategory.indexOf(c) > -1}
                        sx={{ mr: 1 }}
                      />
                      {c}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Document sub category</InputLabel>
                <Select
                  multiple
                  value={documentSubCategory}
                  label="Document sub category"
                  onChange={(e) =>
                    setDocumentSubCategory(
                      typeof e.target.value === "string"
                        ? e.target.value.split(",")
                        : (e.target.value as string[])
                    )
                  }
                  displayEmpty
                  renderValue={(selected) =>
                    (selected as string[]).length
                      ? (selected as string[]).join(", ")
                      : "Choose document sub category"
                  }
                >
                  {documentSubCategories.map((sc) => (
                    <MenuItem key={sc} value={sc}>
                      <Checkbox
                        checked={documentSubCategory.indexOf(sc) > -1}
                        sx={{ mr: 1 }}
                      />
                      {sc}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Policy language</InputLabel>
                <Select
                  multiple
                  value={policyLanguage}
                  label="Policy language"
                  onChange={(e) =>
                    setPolicyLanguage(
                      typeof e.target.value === "string"
                        ? e.target.value.split(",")
                        : (e.target.value as string[])
                    )
                  }
                  displayEmpty
                  renderValue={(selected) =>
                    (selected as string[]).length
                      ? (selected as string[]).join(", ")
                      : "Choose policy language"
                  }
                >
                  {languages.map((lang) => (
                    <MenuItem key={lang} value={lang}>
                      <Checkbox
                        checked={policyLanguage.indexOf(lang) > -1}
                        sx={{ mr: 1 }}
                      />
                      {lang}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <DesktopDatePicker
                label="Published since"
                value={publishedSince}
                onChange={(newValue) => {
                  const asDate = newValue
                    ? newValue instanceof Date
                      ? newValue
                      : typeof (newValue as any).toDate === "function"
                      ? (newValue as any).toDate()
                      : new Date(newValue as any)
                    : null;
                  setPublishedSince(asDate);
                }}
                slotProps={{ textField: { fullWidth: true, size: "small" } }}
              />
            </>
          )}
        </Box>

        <Box
          sx={{
            p: 2,
            borderTop: (theme) => `1px solid ${theme.palette.divider}`,
            display: "flex",
            gap: 1,
          }}
        >
          <Button
            variant="outlined"
            onClick={handleClear}
            sx={{ textTransform: "none" }}
          >
            Clear
          </Button>
          <Button
            variant="contained"
            onClick={handleApply}
            sx={{ textTransform: "none", ml: "auto" }}
          >
            Apply
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default SearchFilter;
